import React, { useState } from 'react';
import Grid from '@mui/material/Grid2';
import { Box, Chip, Divider, FormControl, FormControlLabel, Checkbox, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Tooltip, Typography, Button, } from '@mui/material';
import PropTypes from 'prop-types';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import useTranslation from '../../../../hooks/useTranslation';
import ImportRowEditForm from './ImportRowEditForm';
import { STATUS_CONFIG, STATUS_FILTERS, SEVERITY_COLOR, CATEGORY_LABEL_KEYS, rowStatus } from './importReviewConfig';

const ACTIONS_COLSPAN = 9;

const styles = {
  summaryBar: {
    padding: '4px 24px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  statusChips: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  categoryFilter: {
    minWidth: '200px',
    marginLeft: 'auto',
  },
  tableWrap: {
    padding: '0 24px',
    overflow: 'auto',
    flex: 1,
  },
  issuesCell: {
    minWidth: '220px',
  },
  issueLine: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    fontSize: '0.8rem',
    marginBottom: '2px',
  },
  dimmedRow: {
    '& > td:not(:last-child)': { opacity: 0.55 },
  },
  editFormRow: {
    bgcolor: 'action.hover',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '14px 24px',
  },
  footerActions: {
    display: 'flex',
    gap: '10px',
  },
};

const ImportReviewStep = (props) => {
  const {
    rows, stats, excludedRowIndexes, setExcludedRowIndexes,
    editingRowIndex, editDraft, onEditStart, onEditFieldChange, onEditCancel, onEditSave,
    isInviteChecked, setInviteChecked, onBack, onCommit, loading,
  } = props;
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeCategory, setActiveCategory] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const eligibleRows = rows.filter((row) => !row.blocked);
  const selectedCount = eligibleRows.filter((row) => !excludedRowIndexes.has(row.rowIndex)).length;

  const categoryOptions = [...new Set(rows.flatMap((row) => row.categories))].sort();

  const statusFilteredRows = activeFilter === 'all' ? rows : rows.filter((row) => rowStatus(row) === activeFilter);
  const filteredRows = activeCategory === 'all'
    ? statusFilteredRows
    : statusFilteredRows.filter((row) => row.categories.includes(activeCategory));

  const visibleEligibleRows = filteredRows.filter((row) => !row.blocked);
  const allVisibleEligibleSelected = visibleEligibleRows.length > 0
    && visibleEligibleRows.every((row) => !excludedRowIndexes.has(row.rowIndex));
  const visibleSelectedCount = visibleEligibleRows.filter((row) => !excludedRowIndexes.has(row.rowIndex)).length;

  const paginatedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const selectFilter = (filter) => { setActiveFilter(filter); setPage(0); };
  const selectCategory = (category) => { setActiveCategory(category); setPage(0); };

  const toggleRow = (rowIndex) => {
    setExcludedRowIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  };

  // Only affects the rows currently visible under the active filters, so narrowing to
  // "Needs review" and selecting there won't touch rows in other categories.
  const toggleSelectAll = () => {
    setExcludedRowIndexes((prev) => {
      const next = new Set(prev);
      visibleEligibleRows.forEach((row) => {
        if (allVisibleEligibleSelected) next.add(row.rowIndex);
        else next.delete(row.rowIndex);
      });
      return next;
    });
  };

  const filterChips = stats && [
    { key: 'all', label: `${t('labels.import_preview.all')}: ${stats.total}`, color: 'default' },
    { key: 'ready', label: `${t('labels.import_preview.ready')}: ${stats.ready}`, color: 'success', Icon: STATUS_CONFIG.ready.Icon },
    { key: 'warning', label: `${t('labels.import_preview.needs_review')}: ${stats.warnings}`, color: 'warning', Icon: STATUS_CONFIG.warning.Icon },
    { key: 'existing', label: `${t('labels.import_preview.already_exists')}: ${stats.existing}`, color: 'default', Icon: STATUS_CONFIG.existing.Icon },
    { key: 'error', label: `${t('labels.import_preview.cant_import')}: ${stats.errors}`, color: 'error', Icon: STATUS_CONFIG.error.Icon },
  ];

  return (
    <>
      <Grid size={12} sx={styles.summaryBar}>
        <Box sx={styles.statusChips}>
          {filterChips.map((chip) => (
            <Chip
              key={chip.key}
              label={chip.label}
              color={chip.color}
              size="small"
              variant={activeFilter === chip.key ? 'filled' : 'outlined'}
              onClick={() => selectFilter(chip.key)}
              icon={chip.Icon ? <chip.Icon fontSize="small" /> : undefined}
            />
          ))}
        </Box>
        {categoryOptions.length > 0 && (
          <FormControl size="small" sx={styles.categoryFilter}>
            <InputLabel>{t('labels.import_preview.filter_by_field')}</InputLabel>
            <Select
              value={activeCategory}
              label={t('labels.import_preview.filter_by_field')}
              onChange={(e) => selectCategory(e.target.value)}
            >
              <MenuItem value="all">{t('labels.import_preview.all_fields')}</MenuItem>
              {categoryOptions.map((category) => (
                <MenuItem key={category} value={category}>
                  {t(`labels.import_preview.${CATEGORY_LABEL_KEYS[category] || category}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Grid>
      <Divider width="100%" />
      <Grid size={12} sx={styles.tableWrap}>
        {filteredRows.length === 0 ? (
          <Box sx={{ padding: '32px 0', textAlign: 'center' }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 36, marginBottom: '8px' }} />
            <Typography color="text.secondary">{t('labels.import_preview.no_rows_for_filter')}</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allVisibleEligibleSelected}
                      indeterminate={!allVisibleEligibleSelected && visibleSelectedCount > 0}
                      onChange={toggleSelectAll}
                      title={t('labels.import_preview.select_all_eligible')}
                    />
                  </TableCell>
                  <TableCell>{t('labels.import_preview.row')}</TableCell>
                  <TableCell>{t('labels.import_preview.name')}</TableCell>
                  <TableCell>{t('labels.import_preview.email')}</TableCell>
                  <TableCell>{t('labels.import_preview.role')}</TableCell>
                  <TableCell>{t('labels.import_preview.country')}</TableCell>
                  <TableCell>{t('labels.import_preview.status')}</TableCell>
                  <TableCell sx={styles.issuesCell}>{t('labels.import_preview.issues')}</TableCell>
                  <TableCell align="right">{t('labels.import_preview.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row) => {
                  const status = rowStatus(row);
                  const { color, Icon } = STATUS_CONFIG[status];
                  const checked = !row.blocked && !excludedRowIndexes.has(row.rowIndex);
                  const isEditing = editingRowIndex === row.rowIndex;
                  const issuesByField = Object.fromEntries(row.issues.map((issue) => [issue.field, issue]));
                  const editDisabled = !isEditing && editingRowIndex !== null;
                  const editTooltip = isEditing
                    ? t('labels.import_preview.cancel_edit')
                    : (editDisabled ? t('labels.import_preview.finish_other_edit_first') : t('labels.import_preview.edit_row'));

                  return (
                    <React.Fragment key={row.rowIndex}>
                      <TableRow sx={row.blocked && !isEditing ? styles.dimmedRow : undefined}>
                        <TableCell padding="checkbox">
                          <Tooltip title={status === 'existing' ? t('labels.import_preview.row_already_exists') : (status === 'error' ? t('labels.import_preview.row_cannot_import') : '')}>
                            <span>
                              <Checkbox checked={checked} disabled={row.blocked} onChange={() => toggleRow(row.rowIndex)} />
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{row.rowIndex + 1}</TableCell>
                        <TableCell>{row.data.name || '—'}</TableCell>
                        <TableCell>{row.data.email || '—'}</TableCell>
                        <TableCell>{row.data.role || 'staff'}</TableCell>
                        <TableCell>{row.data.delivery_country || '—'}</TableCell>
                        <TableCell>
                          <Chip size="small" color={color} variant="outlined" icon={<Icon fontSize="small" />} label={t(`labels.import_preview.${STATUS_CONFIG[status].labelKey}`)} />
                        </TableCell>
                        <TableCell sx={styles.issuesCell}>
                          {row.issues.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">{t('labels.import_preview.no_issues')}</Typography>
                          ) : row.issues.map((issue, idx) => (
                            <Box key={idx} sx={styles.issueLine}>
                              <Chip size="small" color={SEVERITY_COLOR[issue.severity] || 'default'} label={issue.field} sx={{ height: '18px', fontSize: '0.65rem' }} />
                              <Typography variant="caption">{issue.message}</Typography>
                            </Box>
                          ))}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={editTooltip}>
                            <span>
                              <Button
                                size="small"
                                startIcon={isEditing ? <CloseIcon fontSize="small" /> : <EditOutlinedIcon fontSize="small" />}
                                onClick={() => (isEditing ? onEditCancel() : onEditStart(row))}
                                disabled={editDisabled}
                              >
                                {isEditing ? t('labels.import_preview.cancel_edit') : t('labels.import_preview.edit_row')}
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      {isEditing && (
                        <TableRow sx={styles.editFormRow}>
                          <TableCell colSpan={ACTIONS_COLSPAN} sx={{ padding: 0 }}>
                            <ImportRowEditForm
                              draft={editDraft}
                              issuesByField={issuesByField}
                              onFieldChange={onEditFieldChange}
                              onSave={onEditSave}
                              onCancel={onEditCancel}
                              saving={loading}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {filteredRows.length > 0 && (
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        )}
      </Grid>
      <Divider width="100%" />
      <Grid size={12} sx={styles.footer}>
        <FormControlLabel
          control={<Checkbox checked={isInviteChecked} onChange={(e) => setInviteChecked(e.target.checked)} />}
          label={t('labels.invite_user')}
        />
        <Box sx={styles.footerActions}>
          <Button variant="outlined" onClick={onBack}>{t('labels.import_preview.back')}</Button>
          <Button variant="contained" color="primary" onClick={onCommit} disabled={selectedCount === 0 || editingRowIndex !== null}>
            {t('labels.import_preview.import_selected')}
          </Button>
        </Box>
      </Grid>
    </>
  );
};

ImportReviewStep.propTypes = {
  rows: PropTypes.array.isRequired,
  stats: PropTypes.object,
  excludedRowIndexes: PropTypes.instanceOf(Set).isRequired,
  setExcludedRowIndexes: PropTypes.func.isRequired,
  editingRowIndex: PropTypes.number,
  editDraft: PropTypes.object,
  onEditStart: PropTypes.func.isRequired,
  onEditFieldChange: PropTypes.func.isRequired,
  onEditCancel: PropTypes.func.isRequired,
  onEditSave: PropTypes.func.isRequired,
  isInviteChecked: PropTypes.bool.isRequired,
  setInviteChecked: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onCommit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default ImportReviewStep;
